import { useEffect, useMemo, useState } from 'react'
import * as actions from '@actions'
import * as store from '@store'
import { toast } from 'react-toastify'
import { relativeTime } from '@/utils/date'
import usePollAtrStatus from './usePollAtrStatus'
import { useWireValue } from '@forminator/react-wire'
import { CAN_PROCESS_ATR, HUMAN_ATR_STATUS } from '@/constants/atr'
import CustomModal, { ModalBody, ModalFooter, ModalHeader } from '../shared/CustomModal'
import DocumentExtractModal from './DocumentExtractModal'
import { useSearchParams } from 'react-router-dom'

const TEXT_LEN = 50

const ATR = () => {
    const [searchParams, setSearchParams] = useSearchParams()
    
    const apiConfig = useWireValue(store.apiConfig)
    
    const packets = useWireValue(store.atrPackets)
    const [selectedPkt, setSelectedPkt] = useState(null)
    const [processingIds, setProcessingIds] = useState(new Set())
    const id = searchParams.get('id')
    const folderId = searchParams.get('folderId')
    const selectedPktId = searchParams.get('selectedPktId')
    
    const documentExtractModal = useMemo(() => searchParams.get('modal') === 'true', [searchParams.get('modal')])
    
    const isAtrModalEnabled = useMemo(() => !!apiConfig?.remoteConfig?.atr?.documentEdit, [apiConfig])
    
    const cropMessage = msg => msg.length > TEXT_LEN
        ? msg.slice(0, TEXT_LEN).split('\\n').join(' ') + '...'
        : msg.split('\\n').join(' ')
    
    const parseIfNeeded = (msg, snippet = false) => {
        
        if (!msg) return msg
        
        try {
            const parsed = JSON.parse(msg)
            
            if (snippet) return Object.keys(parsed).join(', ')
            
            return Object.keys(parsed).map(key => <div key={key} className="flex flex-col mb-4">
                <label className="font-bold">{key}</label>
                <ul className="pl-6 list-disc">
                    {parsed[key].map((value, idx) => <li key={idx}>{value}</li>)}
                </ul>
            </div>,
                
            )
        } catch (_) {
            return msg
        }
    }
    
    const startAtrProcess = async pkt => {
        
        try {
            const piBefore = new Set(processingIds)
            
            piBefore.add(pkt.id)
            setProcessingIds(piBefore)
            await actions.processAtrFile(pkt.id)
            
            toast.success(`Processing started for "${pkt.transactionId}".`)
        } catch (e) {
            
            if (e.response?.status === 409)
                toast.error(e.response?.data.error || `Failed to process transaction "${pkt.transactionId}".`)
            else
                toast.error(`Failed to process transaction "${pkt.transactionId}".`)
            
        } finally {
            const piAfter = new Set(processingIds)
            
            piAfter.delete(pkt.id)
            setProcessingIds(piAfter)
        }
    }
    
    const openDeModal = (id, folderId, selectedPktId) => {
        setSearchParams(prev => {
            prev.set('modal', 'true')
            prev.set('id', id)
            prev.set('folderId', folderId)
            prev.set('selectedPktId', selectedPktId)
            return prev
        })
    }
    
    usePollAtrStatus(packets, selectedPktId)
    
    useEffect(() => {
        
        actions.getPackets().catch(() => {
            toast.error('Failed to fetch the list of packets.')
        })
        
    }, [])
    
    
    return (
        <div className="m-4 overflow-auto max-h-[calc(100vh-100px)]">
            <table className="table table-sm table-pin-rows">
                <thead>
                    <tr>
                        <th className="w-1/3">Transaction</th>
                        <th className="w-1/3">Last Run Status</th>
                        <th>Locked By</th>
                        <th>Started By</th>
                        <th>Created At</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {packets.map(pkt =>
                        <tr key={pkt.id}>
                            <td>
                                {pkt.folderId && (
                                    isAtrModalEnabled ? (
                                        <button 
                                            className="btn btn-link !p-0"
                                            onClick={() => {
                                                openDeModal(pkt.transactionId, pkt.folderId, pkt.id)
                                            }}>
                                            {pkt.transactionId}
                                        </button>
                                    ) : (
                                        <a className="btn btn-link !p-0"
                                            target="_blank"
                                            href={`https://drive.google.com/drive/folders/${pkt.folderId}`}>
                                            {pkt.transactionId}
                                        </a>
                                    ) 
                                )}
                                {!pkt.folderId && pkt.transactionId}
                            </td>
                            <td>
                                <div>{HUMAN_ATR_STATUS[pkt.status] || pkt.status}</div>
                                {pkt.statusDetail && <div>
                                    <div className="line-clamp-1 text-base-content/60 text-[13px]">
                                        <span className="font-bold">Errors: </span>
                                        {cropMessage(parseIfNeeded(pkt.statusDetail, true))}
                                    </div>
                                    {pkt.statusDetail.length > TEXT_LEN &&
                                        <span
                                            className="cursor-pointer btn btn-link btn-xs !p-0"
                                            onClick={() => setSelectedPkt(pkt)}>See more...</span>
                                    }
                                </div>
                                }
                            </td>
                            <td>{pkt.userLock?.firstName} {pkt.userLock?.lastName}</td>
                            <td>{pkt.userName || 'ATR'}</td>
                            <td>{relativeTime(pkt.createdAt)}</td>
                            <td>
                                <button className="btn btn-primary btn-xs"
                                    disabled={!CAN_PROCESS_ATR.includes(pkt.status) || processingIds.has(pkt.id)
                                        || pkt.requestProcessing
                                        || pkt.lockedById}
                                    onClick={() => startAtrProcess(pkt)}>
                                    {processingIds.has(pkt.id) &&
                                        <span className="loading loading-spinner loading-xs" />}
                                    Process
                                </button>
                            </td>
                        </tr>,
                    )}
                </tbody>
            </table>
            
            {selectedPkt && <CustomModal open={true} modalHandler={() => setSelectedPkt(null)}>
                <ModalHeader>
                    <div>Error Detail</div>
                    <div className="text-sm text-base-content/60">Packet: {selectedPkt.transactionId}</div>
                </ModalHeader>
                <ModalBody>
                    <div className="py-1 whitespace-pre-wrap">
                        {parseIfNeeded(selectedPkt.statusDetail)}
                    </div>
                </ModalBody>
                <ModalFooter>
                    <button className="btn btn-secondary" onClick={() => setSelectedPkt(null)}>Close</button>
                </ModalFooter>
            </CustomModal>}
            
            {documentExtractModal && (
                <DocumentExtractModal
                    documentExtractModal={documentExtractModal}
                    transactionId={id}
                    folderId={folderId}
                    selectedPktId={selectedPktId}
                    setSelectedPkt={setSelectedPkt}/>
            )}
        </div>
        
    )
}
export default ATR